import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import newIdempotencyKey from 'src/utils/idempotencyKey';
import { formatUkPhone } from 'src/utils/ukPhone';
import { linkCall } from 'src/pages/dashboard/callLogs/caller/callerActions.api';
import { includeEarlierCalls } from './callFlowModel';
import useLeadSuggestions, { MIN_SEARCH_LENGTH } from './useLeadSuggestions';
import LeadMatchList from './LeadMatchList';
import { callLayer } from '../callPill/callLayers';

// Link to existing (R5): suggestions load at once, a search box finds anyone else, and
// "Create lead instead" hands over to Create lead. Linking also links the number's
// earlier calls (unless the number is shared). The host then refreshes the lists.
export default function LinkLeadDialog({ practiceId, call, onClose, onLinked, onCreateInstead }) {
    const [query, setQuery] = useState('');
    const state = useLeadSuggestions({ practiceId, call, query });
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState({ text: '', uncertain: false });
    // One key per link intent (lead + unarchive + earlier), reused if it is pressed again after a refusal.
    const keys = useRef({});
    const includeEarlier = includeEarlierCalls(call);
    const typed = query.trim().length >= MIN_SEARCH_LENGTH;
    const entries = useMemo(() => (typed
        ? state.results.map((lead) => ({ lead, reasons: [] }))
        : state.suggestions), [typed, state.results, state.suggestions]);

    const choose = async ({ lead, unarchive }) => {
        if (busy || message.uncertain) return;
        setBusy(true);
        setMessage({ text: '', uncertain: false });
        const args = { leadId: lead.id, unarchive, includeEarlier };
        const sig = JSON.stringify(args);
        keys.current[sig] = keys.current[sig] || newIdempotencyKey();
        const result = await linkCall(practiceId, call.row, args, keys.current[sig]);
        setBusy(false);
        if (result.ok) { onLinked(lead); return; }
        // No answer / a 5xx: the link may have saved, so never say "failed" and never invite a blind retry.
        setMessage(result.uncertain ? { text: '', uncertain: true } : { text: result.message, uncertain: false });
    };

    return (
        <Dialog open onClose={busy ? undefined : () => onClose(message.uncertain)} fullWidth maxWidth="sm" aria-labelledby="link-lead-title" sx={{ zIndex: callLayer('DIALOG') }}>
            <DialogTitle id="link-lead-title">Link to existing lead</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        {call.number ? `Who called from ${formatUkPhone(call.number)}?` : 'Who made this call?'} Pick the lead to link{includeEarlier ? ' this call and earlier calls from the number' : ' this call'} to.
                    </Typography>
                    <TextField
                        size="small"
                        autoFocus
                        placeholder="Search by name, phone or email"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" width={18} /></InputAdornment>,
                            inputProps: { 'aria-label': 'Search leads' },
                        }}
                    />
                    {state.status === 'loading' && <Typography variant="body2" color="text.secondary" role="status">{typed ? 'Searching…' : 'Looking for likely matches…'}</Typography>}
                    {state.status === 'error' && <Alert severity="warning">Matches could not be loaded. Search by name or phone instead.</Alert>}
                    {state.status === 'ready' && entries.length === 0 && (
                        <Typography variant="body2" color="text.secondary" data-testid="link-lead-empty">
                            {typed ? 'Nobody matches that search.' : 'No likely matches. Search above, or create a lead.'}
                        </Typography>
                    )}
                    {entries.length > 0 && <LeadMatchList entries={entries} busy={busy} confirmLabel="Link" onChoose={choose} />}
                    {message.text && <Alert severity="error">{message.text}</Alert>}
                    {message.uncertain && <Alert severity="warning">We couldn&apos;t confirm the call was linked. Close this and refresh the list to check before trying again.</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button onClick={onCreateInstead} disabled={busy} startIcon={<Iconify icon="eva:person-add-fill" width={18} />}>Create lead instead</Button>
                <Button onClick={() => onClose(message.uncertain)} disabled={busy}>{message.uncertain ? 'Close and refresh' : 'Cancel'}</Button>
            </DialogActions>
        </Dialog>
    );
}

LinkLeadDialog.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    call: PropTypes.shape({ id: PropTypes.string.isRequired, number: PropTypes.string, row: PropTypes.object.isRequired }).isRequired,
    // (uncertain) true when a link may have saved: the host reloads the lists.
    onClose: PropTypes.func.isRequired,
    // (lead) once linked.
    onLinked: PropTypes.func.isRequired,
    onCreateInstead: PropTypes.func.isRequired,
};
